import React, { useEffect, useState } from "react";
import { Modal, Button, Progress } from "antd";

const ServiceWorkerPopup = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    console.log("[Popup] Component mounted, setting up listeners");
    
    // Listen to update notifications
    const handleSWUpdate = (event) => {
      console.log("[Popup] SW update event received:", event.detail);
      if (event.detail?.type === "UPDATE_AVAILABLE") {
        setUpdateAvailable(true);
      }
    };
    
    // Listen to install/update progress messages
    const handleSWMessage = (event) => {
      console.log("[Popup] SW message received:", event.detail);
      const { type, progress: reportedProgress } = event.detail || {};
      
      switch (type) {
        case "INSTALL_PROGRESS":
          console.log(`[Popup] Setting progress to ${reportedProgress}%`);
          setProgress(reportedProgress);
          break;
        case "INSTALL_COMPLETE":
          console.log("[Popup] Install complete, setting progress to 100%");
          setProgress(100);
          // The service worker will handle page reload
          break;
        case "INSTALL_START":
          console.log("[Popup] Install starting");
          setIsUpdating(true);
          setProgress(0);
          break;
        case "UPDATE_ACTIVATED":
          console.log("[Popup] Update activated");
          // Ensure we show 100% before reload
          setProgress(100);
          break;
        default:
          break;
      }
    };
    
    // Additional direct progress listener for better reliability
    const handleSWProgress = (event) => {
      const { progress: reportedProgress } = event.detail || {};
      console.log(`[Popup] Direct progress update: ${reportedProgress}%`);
      if (typeof reportedProgress === 'number') {
        setProgress(reportedProgress);
      }
    };
    
    // Add all event listeners
    window.addEventListener("sw-update", handleSWUpdate);
    window.addEventListener("sw-message", handleSWMessage);
    window.addEventListener("sw-progress", handleSWProgress);
    
    // Debug log to confirm component is listening
    console.log("[Popup] All event listeners are set up");
    
    return () => {
      window.removeEventListener("sw-update", handleSWUpdate);
      window.removeEventListener("sw-message", handleSWMessage);
      window.removeEventListener("sw-progress", handleSWProgress);
    };
  }, []);

  const handleUpdate = () => {
    console.log("[Popup] Update button clicked");
    
    // Set updating state immediately for better UI feedback
    setIsUpdating(true);
    setProgress(0);
    
    // Small delay before triggering skipWaiting to ensure UI updates first
    setTimeout(() => {
      if (window.__SW_SKIP_WAITING__) {
        console.log("[Popup] Calling __SW_SKIP_WAITING__");
        window.__SW_SKIP_WAITING__();
      } else {
        console.error("[Popup] __SW_SKIP_WAITING__ function not available");
      }
    }, 100);
  };
  
  // For debugging - log state changes
  useEffect(() => {
    console.log(
      `[Popup] State updated - updateAvailable: ${updateAvailable}, isUpdating: ${isUpdating}, progress: ${progress}`
    );
  }, [updateAvailable, isUpdating, progress]);
  
  const showModal = updateAvailable || isUpdating;
  
  return (
    <Modal
      open={showModal}
      title={isUpdating ? "Updating..." : "New Version Available"}
      closable={false}
      footer={
        !isUpdating && (
          <Button type="primary" onClick={handleUpdate}>
            Update Now
          </Button>
        )
      }
      centered
      maskClosable={false}
    >
      {isUpdating ? (
        <>
          <p>Please wait while we update the application.</p>
          <Progress
            percent={progress}
            status={progress < 100 ? "active" : "success"}
            showInfo
            strokeWidth={10}
          />
        </>
      ) : (
        <p>A new version of the app is ready. Click below to update now.</p>
      )}
    </Modal>
  );
};

export default ServiceWorkerPopup;